<script setup>
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import axios from 'axios';
import { BASE_URL, BASE_URL_2, BASE_URL_5 } from '@/Config/config';
import { useStore } from 'vuex';
import Swal from 'sweetalert2';

const store = useStore();
const route = useRoute();
const router = useRouter();

// Reactive variables
const isLoading = ref(false);
const error = ref(null);
const warningLetter = ref(null);
const violations = ref([]);
const documentFile = ref(null);
const isUploading = ref(false);
const trucks = ref([]);
const trailers = ref([]);
const drivers = ref([]);

const showSuccessAlert = (message) => {
    Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: message,
        padding: '2em',
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false
    });
};

const showInfoAlert = (message) => {
    Swal.fire({
        icon: 'info',
        title: 'Info',
        text: message,
        padding: '2em',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK'
    });
};

const showErrorAlert = (message) => {
    Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: message,
        padding: '2em',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK'
    });
};

const showConfirmDialog = (title, message) => {
    return Swal.fire({
        title: title,
        text: message,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, proceed',
        cancelButtonText: 'Cancel'
    });
};

const loadEmployees = async () => {
    try {
        const response = await axios.get(`${BASE_URL_2}/employee/driver/show`);
        drivers.value = response.data.drivers;
        console.log("Drivers loaded:", drivers.value);
    } catch (error) {
        console.error('Error fetching employees:', error);
        showErrorAlert('Failed to load drivers. Please try again later.');
    }
};

// Helper function to get driver name by employee ID
const getDriverName = (employeeId) => {
    const driver = drivers.value.find(d => d.employee_id == employeeId);
    return driver?.driver || 'N/A';
};

const loadTrucks = async () => {
    try {
        const response = await axios.get(`${BASE_URL_2}/vehicle/truck/show`);
        trucks.value = response.data.trucks || response.data.data || response.data;
        console.log("Trucks loaded:", trucks.value);
    } catch (error) {
        console.error('Error fetching trucks:', error);
        showErrorAlert('Failed to load trucks. Please try again later.');
    }
};

// Helper function to get truck registration number by truck ID
const getTruckNumber = (truckId) => {
    const truck = trucks.value.find(t => t.id == truckId);
    return truck?.registration_number || 'N/A';
};

const loadTrailers = async () => {
    try {
        const response = await axios.get(`${BASE_URL_2}/vehicle/trailer/show`);
        trailers.value = response.data.trailers || response.data.data || response.data;
        console.log("Trailers loaded:", trailers.value);
    } catch (error) {
        console.error('Error fetching trailers:', error);
        showErrorAlert('Failed to load trailers. Please try again later.');
    }
};

// Helper function to get trailer registration number by trailer ID
const getTrailerNumber = (trailerId) => {
    const trailer = trailers.value.find(t => t.id == trailerId);
    return trailer?.registration_number || 'N/A';
};

const loadData = async () => {
    error.value = null;

    try {
        const response = await axios.get(`${BASE_URL}/violation-actions/warning-letters/${route.params.id}`, {
            headers: {
                'Authorization': `Bearer ${store.state.token}`,
                'X-Company-ID': store.state.currentCompany?.id
            }
        });

        warningLetter.value = response.data.data.letter;
        violations.value = response.data.data.violations || [];
        console.log('Warning Letter:', warningLetter.value);
        console.log('Violations:', violations.value);
    } catch (err) {
        error.value = err.message || 'Failed to load warning letter details';
    } finally {
        isLoading.value = false;
    }
};

const handleFileChange = (e) => {
    documentFile.value = e.target.files[0];
};

const uploadDocument = async () => {
    if (!documentFile.value) {
        showInfoAlert('Please select a document to upload!');
        return;
    }

    try {
        isUploading.value = true;
        const formData = new FormData();
        formData.append('document', documentFile.value);

        await axios.post(
            `${BASE_URL}/violation-actions/warning-letters/upload/${route.params.id}`,
            formData,
            {
                headers: {
                    'Authorization': `Bearer ${store.state.token}`,
                    'X-Company-ID': store.state.currentCompany?.id,
                    'Content-Type': 'multipart/form-data'
                }
            }
        );

        showSuccessAlert('Document Uploaded Successfully!');
        await loadData(); // Refresh the data
        documentFile.value = null;
    } catch (err) {
        showErrorAlert(err.response?.data?.message || 'Imeshindikana kupakia faili');
    } finally {
        isUploading.value = false;
    }
};

const downloadDocument = async () => {
    try {
        // Show loading alert
        Swal.fire({
            title: 'Downloading...',
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        const response = await axios.get(
            `${BASE_URL}/violation-actions/warning-letters/download/${route.params.id}`,
            {
                headers: {
                    'Authorization': `Bearer ${store.state.token}`,
                    'X-Company-ID': store.state.currentCompany?.id
                },
                responseType: 'blob'
            }
        );

        const mimeType = response.headers['content-type'];
        const extension = mimeType.split('/')[1] || 'bin';

        // Create a blob URL for the file
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Warning_Letter_${warningLetter.value.employee_id}.${extension}`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        Swal.close();
    } catch (err) {
        Swal.close();
        showErrorAlert(err.response?.data?.message || 'Failed to download document');
    }
};

// Update your existing getDocumentUrl method to ensure proper URL
const getDocumentUrl = (path) => {
    if (!path) return '#';
    // If path already has full URL, return as-is
    if (path.startsWith('http')) return path;
    // Otherwise construct from base URL
    return `${BASE_URL_5}/storage/${path}`;
};

const formatDate = (dateString) => {
    if (!dateString) return '—';
    const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    };
    return new Date(dateString).toLocaleString(undefined, options);
};

const printWarningLetter = () => {
    const printWindow = window.open('', '', 'width=800,height=900');
    if (!printWindow) {
        showErrorAlert('Could not open print window. Please allow popups for this site.');
        return;
    }

    const driverName = getDriverName(warningLetter.value.employee_id) || 'Unknown Driver';
    const employeeId = warningLetter.value.employee_id;
    const issuedDate = new Date(warningLetter.value.created_at).toLocaleDateString('en-GB');
    const period = `${formatDate(warningLetter.value.start_date)} to ${formatDate(warningLetter.value.end_date)}`;
    const issuerName = warningLetter.value.issuer?.name || 'Unknown';

    // Create violations table HTML
    const violationsTable = violations.value.length > 0 ? `
        <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
            <thead>
                <tr style="background-color: #f2f2f2;">
                    <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">#</th>
                    <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Tarehe</th>
                    <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Aina ya Ukosefu</th>
                    <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Namba ya Lori</th>
                    <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Namba ya Trail</th>
                </tr>
            </thead>
            <tbody>
                ${violations.value.map((violation, index) => `
                    <tr>
                        <td style="border: 1px solid #ddd; padding: 8px;">${index + 1}</td>
                        <td style="border: 1px solid #ddd; padding: 8px;">${formatDate(violation.created_at)}</td>
                        <td style="border: 1px solid #ddd; padding: 8px;">${violation.violation?.title || 'N/A'}</td>
                        <td style="border: 1px solid #ddd; padding: 8px;">${getTruckNumber(violation.truck_id) || 'N/A'}</td>
                        <td style="border: 1px solid #ddd; padding: 8px;">${getTrailerNumber(violation.trailer_id) || 'N/A'}</td>
                    </tr>
                `).join('')}
            </tbody>
        </table>
    ` : '<p>Hakuna ukosefu uliorekodiwa katika kipindi hiki.</p>';

    printWindow.document.write(`
        <html>
            <head>
                <title>Warning Letter - ${driverName}</title>
                <style>
                    @page {
                        size: A4 portrait;
                        margin: 10pt;
                    }
                    body {
                        font-family: Arial, sans-serif;
                        line-height: 1.6;
                        font-size: 7px;
                        padding: 15px;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }
                    .letter-head {
                        text-align: center;
                        margin-bottom: 20px;
                        border-bottom: 2px solid #000;
                        padding-bottom: 20px;
                    }
                    .letter-head .title {
                        font-size: 24px;
                        font-weight: bold;
                        color: red;
                    }
                    .letter-content {
                        margin: 30px 0;
                    }
                    .letter-footer {
                        margin-top: 50px;
                    }
                    .footer {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-top: 40px;
                        border-top: 2px solid #000;
                        padding-top: 10px;
                        font-size: 10px;
                        flex-wrap: wrap;
                        gap: 10px;
                    }
                    .footer > div {
                        min-width: 18%;
                    }
                    .underline {
                        text-decoration: underline;
                    }
                    .bold {
                        font-weight: bold;
                    }
                    .mt-4 {
                        margin-top: 1.5rem;
                    }
                    .mb-2 {
                        margin-bottom: 0.5rem;
                    }
                </style>
            </head>
            <body>
                <div class="letter-head">
                    <div class="title bold">MA CARGO TRUCKERS & FORWARDERS COMPANY LIMITED</div>
                    <div>P.O Box 678, Morogoro Tanzania</div>
                    <div>Plot No. 9-14 Block C Msamvu Industrial Area, Tanzania</div>
                    <div>Mob: +255 (0) 23 261 3589</div>
                    <div>Email: info@marcargo.co.tz</div>
                </div>

                <div class="letter-content">
                    <div style="text-align: right;">Tarehe: ${issuedDate}</div>
                    
                    <p class="bold">Kwa: ${driverName.toUpperCase()}</p>
                    <p class="bold">Dereva – M.A Cargo Truckers & Forwarders Company Limited.</p>
                    
                    <p class="bold mt-4">Ndugu,</p>
                    
                    <p class="bold underline">YAH: - ONYO KALI JUU YA MWENDOKASI NA KUENDESHA GARI USIKU.</p>
                    
                    <p class="mt-4">
                        Ikumbukwe kuwa hivi karibuni umekuwa na mwenendo mbaya wa utendaji kazi wako kwa kukiuka taratibu zilizowekwa na ofisi kwa makusudi ukijua kuwa ni kosa. Baadhi ya taratibu ambazo umekuwa ukizikiuka ni kama zifuatazo:-
                    </p>
                    
                    <ol>
                        <li>Kuendesha gari kwa mwendokasi wakati unajua wazi ni kosa na kinyume na taratibu za ofisi hali ambayo imekuwa ikijirudia mara kwa mara.</li>
                        <li>Kuendesha gari baada ya saa kumi na mbili jioni (kuendesha usiku) ili hali unafahamu vizuri kuwa ni kinyume na taratibu za ofisi.</li>
                    </ol>
                    
                    <p class="mt-4">
                        Kuanzia sasa upo chini ya uangalizi mkubwa na wa karibu toka ofisini kuhusiana na mwenendo wako huo mbovu unaoleta taswira mbaya kwa kampuni. 
                    </p>
                    
                    <p>
                        Endapo utashindwa kujirekebisha na kurudia makosa hayo basi Kampuni haitasita kukuchukulia hatua kali za kinidhamu ikiwemo kushushwa na kuwekwa benchi au kusitishwa mkataba wako wa ajira.
                    </p>
                    
                    <div class="mt-4">
                        <p class="bold">Ukosefu Uliofanywa:</p>
                        ${violationsTable}
                    </div>
                    
                    <p class="mt-4">Nawasilisha.</p>
                    
                    <p class="bold">Meneja Utumishi na Utawala</p>
                    
                    <div class="letter-footer">
                        <p class="bold underline">UTHIBITISHO</p>
                        
                        <p class="mb-2">
                            Mimi <span class="underline">${driverName}</span> nathibitisha kuwa nimesoma na kuelewa haya maelekezo ya ofisi na kuwa nitayatekeleza. Nathibitisha haya kwa kusaini hapa chini.
                        </p>
                        
                        <div class="footer">
                            <div>Prepared By: ____________________<br>Tracking Officer</div>
                            <div>____________________<br>Operation Officer</div>
                            <div>____________________<br>QHSE</div>
                            <div>_____________________<br>HR</div>
                            <div>_____________________<br>General Manager</div>
                        </div>
                    </div>
                </div>
                
                <script>
                    window.onload = function() {
                        setTimeout(() => {
                            window.print();
                            setTimeout(() => {
                                window.close();
                            }, 500);
                        }, 200);
                    };
                <\/script>
            </body>
        </html>
    `);

    printWindow.document.close();
    printWindow.focus();
};

onMounted(async () => {
    isLoading.value = true;
    error.value = null;

    try {
        await Promise.all([
            loadData(),
            loadTrucks(),
            loadTrailers(),
            loadEmployees()
        ]);
    } catch (err) {
        showErrorAlert(err.message || 'Failed to load Data');
    } finally {
        isLoading.value = false;
    }
});
</script>

<template>
    <div class="layout-px-spacing">
        <div class="row layout-top-spacing">
            <div class="col-xl-12 col-lg-12 col-md-12 col-12 layout-spacing">
                <div class="panel br-4">
                    <div class="panel-body">
                        <div class="d-flex justify-content-between align-items-center mb-4">
                            <h3 class="mb-0">Warning Letter Details</h3>
                            <button class="btn btn-secondary"
                                @click="router.push('/violation/action/warning-letters/list')">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round" class="feather feather-arrow-left">
                                    <line x1="19" y1="12" x2="5" y2="12"></line>
                                    <polyline points="12 19 5 12 12 5"></polyline>
                                </svg>
                                Back to List
                            </button>
                        </div>

                        <div v-if="error" class="alert alert-danger">
                            {{ error }}
                        </div>

                        <div v-else-if="isLoading" class="text-center p-4">
                            <div class="spinner-border text-primary" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                        </div>

                        <div v-else-if="!warningLetter" class="alert alert-info">
                            Warning letter not found.
                        </div>

                        <div v-else>
                            <div class="card">
                                <div class="card-header">
                                    <h5 class="mb-0">Document Management</h5>
                                </div>
                                <div class="card-body">
                                    <div v-if="warningLetter.document_path" class="mb-4">
                                        <a :href="getDocumentUrl(warningLetter.document_path)" target="_blank">
                                            View attached document
                                        </a>
                                        <p><strong>Uploaded Document:</strong></p>
                                        <button class="btn btn-primary" @click="downloadDocument">
                                            Download Signed Copy
                                        </button>
                                    </div>

                                    <div>
                                        <h6>Upload Signed Document</h6>
                                        <div class="input-group mb-3">
                                            <input type="file" class="form-control" accept=".pdf,.jpg,.png"
                                                @change="handleFileChange">
                                            <button class="btn btn-success" @click="uploadDocument"
                                                :disabled="!documentFile || isUploading">
                                                <span v-if="isUploading"
                                                    class="spinner-border spinner-border-sm"></span>
                                                {{ isUploading ? 'Uploading...' : 'Upload' }}
                                            </button>
                                        </div>
                                        <small class="text-muted">Accepted formats: PDF, JPG, PNG (Max 2MB)</small>
                                    </div>
                                </div>
                            </div>

                            <div class="mt-4 d-flex justify-content-between mb-2">
                                <button class="btn btn-info" @click="printWarningLetter">
                                    Print Warning Letter
                                </button>
                            </div>

                            <div class="card mb-4">
                                <div class="card-body">
                                    <h4 class="card-title">Warning Letter Information</h4>
                                    <div class="row">
                                        <div class="col-md-6">
                                            <p><strong>Driver:</strong> {{ getDriverName(warningLetter.employee_id) ||
                                                'N/A' }}</p>
                                            <p><strong>Employee ID:</strong> {{ warningLetter.employee_id }}</p>
                                            <p><strong>Issued On:</strong> {{ formatDate(warningLetter.created_at) }}
                                            </p>
                                        </div>
                                        <div class="col-md-6">
                                            <p><strong>Period:</strong> {{ formatDate(warningLetter.start_date) }} to {{
                                                formatDate(warningLetter.end_date) }}</p>
                                            <p><strong>Issued By:</strong> {{ warningLetter.issued_by?.username || 'N/A'
                                                }}</p>
                                            <p><strong>Violations Count:</strong> {{ warningLetter.violation_ids?.length
                                                || 0 }}</p>
                                        </div>
                                    </div>
                                    <div class="mt-3">
                                        <p><strong>Reason:</strong></p>
                                        <p>{{ warningLetter.reason }}</p>
                                    </div>
                                </div>
                            </div>

                            <div class="card mb-4">
                                <div class="card-header">
                                    <h5 class="mb-0">Violations</h5>
                                </div>
                                <div class="card-body">
                                    <div class="table-responsive">
                                        <table class="table table-bordered">
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>Date</th>
                                                    <th>Violation</th>
                                                    <th>Truck</th>
                                                    <th>Trailer</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="(violation, index) in violations" :key="violation.id">
                                                    <td>{{ index + 1 }}</td>
                                                    <td>{{ formatDate(violation.created_at) }}</td>
                                                    <td>{{ violation.violation?.title || 'N/A' }}</td>
                                                    <td>{{ getTruckNumber(violation.truck_id) || 'N/A' }}</td>
                                                    <td>{{ getTrailerNumber(violation.trailer_id) || 'N/A' }}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.card {
    margin-bottom: 20px;
    border: 1px solid #ddd;
    border-radius: 4px;
}

.card-header {
    background-color: #f8f9fa;
    padding: 10px 15px;
    border-bottom: 1px solid #ddd;
}

.card-title {
    margin-bottom: 15px;
    color: #333;
}

.table th {
    background-color: #f8f9fa !important;
}
</style>